import _ from 'lodash';
import clone from 'clone';

/**
 * Deep cloning the entire state is rarely the desired functionality, especially
 * if there are large chunks of data in the state causing the clone to be slow.
 * Keep in mind that React does pointer comparison to see if an object has changed
 * so shallow cloning all objects in the path notifies React of changes along that path.
 * Methodology
 * 		1) do a shallow clone of the state
 * 		2) shallow clone the object that is changing and all the objects in the path to that object
 * 		3) deep clone the new value
 *
 * note: if path to the object doesn't exist then it won't be created; but if the field in the object doesn't exist then it will be created
 *
 * ie field not there
 * state = { a: { b: { c: 3 } } }
 * path = a.b
 * field = d
 * value = 4
 * result: { a: { b: { d: 4, c: 3 } } }
 * note that the 'd' field did get created
 *
 * ie object not there
 * state = { a: { b: { c: 3 } } }
 * path = a.b.d
 * field = e
 * value = 4
 * result: { a: { b: { c: 3 } } }
 * note that the 'e' field did not get created because there is no 'd' object
 *
 */
export default function objectAtPathReducer(state, action) {
	// not a deep clone; creates new object so react sees a change
	const result = {...state};

	// payload can be an array of targets or a single one
	_.castArray(action.payload).forEach(payload => {
		const path = payload && payload.path;
		const field = payload && payload.field;
		const value = payload && clone(payload.value);

		if (path) {
			const pathPieces = _.split(path, '\.');
			// shallow clone all objects in the path
			const targetObject = pathPieces.reduce((nextLevel, pathPiece) => {
				// if current level isn't an object then childObj is undefined (can't get a field out of a non-object)
				let childObj = undefined;
				if (_.isObject(nextLevel)) {
					childObj = nextLevel[pathPiece];
					if (childObj !== undefined) {
						// no need to clone a non-object
						if (_.isObject(childObj)) {
							if (_.isArray(childObj)) {
								childObj = childObj.concat([]);
							} else {
								childObj = {...childObj};
							}
							// put clone back in state so that the pointers change which will trigger react to render
							nextLevel[pathPiece] = childObj;
						}
					}
				}
				return childObj;
			}, result);

			// set value of target object
			if (targetObject && _.isObject(targetObject)) {
				// set deep cloned value
				targetObject[field] = value;
			}
		} else if (field) {
			// set field on root state object
			result[field] = value;
		}
	});

	return result;
}
